import { infer as Infer, input as Input, z } from 'zod';

import { gradeMappingSchema } from '#grade-scale/gradeScale.schema';
import { groupPublicToId } from '#group/groupPublic.schema';
import { quizPublicToId } from '#quiz/quizPublic.schema';
import { userPublicToId } from '#user/userPublic.schema';

const sharedAssignmentFields = z.object({
  name: z.string().trim().min(2, 'Nazwa musi mieć co najmniej 2 znaki').max(100, 'Nazwa nie może przekraczać 100 znaków'),
  desc: z.string().trim().max(2000, 'Opis nie może przekraczać 2000 znaków'),
  available: z.iso.datetime('Nieprawidłowa data').nullish(),
  dueTo: z.iso.datetime('Nieprawidłowa data').nullish(),
  maxAttempts: z.number().int('Liczba prób musi być liczbą całkowitą').min(0, 'Liczba prób musi być większa lub równa 0'),
  autoGrade: z.boolean(),
  gradeMapping: gradeMappingSchema.nullable(),
});

const dueToAfterAvailable = (data: { available?: string | null; dueTo?: string | null }): boolean => {
  if (!data.available || !data.dueTo) return true;
  return new Date(data.dueTo).getTime() >= new Date(data.available).getTime();
};

export const assignmentBuilderSchema = sharedAssignmentFields
  .extend({
    maxAttempts: sharedAssignmentFields.shape.maxAttempts.default(0),
    autoGrade: sharedAssignmentFields.shape.autoGrade.default(false),
    gradeMapping: sharedAssignmentFields.shape.gradeMapping.default(null),
    users: z.array(userPublicToId).default([]),
    groups: z.array(groupPublicToId).default([]),
    quiz: quizPublicToId.nullable().optional(),
  })
  .refine(dueToAfterAvailable, {
    message: 'Data zakończenia musi być po dacie dostępności',
    path: ['dueTo'],
  })
  // Form state uses rich `users` / `groups` / `quiz`; the wire payload uses the
  // backend field names `userIdArr` / `groupIdArr` / `quizId`.
  .transform(({ users, groups, quiz, ...rest }) => ({
    ...rest,
    userIdArr: users,
    groupIdArr: groups,
    quizId: quiz ?? null,
  }));

export const assignmentEditSchema = sharedAssignmentFields
  .partial()
  .extend({
    id: z.number().int().positive(),
    quizId: z.string().uuid().nullable().optional(),
    addUserIdArr: z.array(z.number().int().positive()).optional(),
    removeUserIdArr: z.array(z.number().int().positive()).optional(),
    addGroupIdArr: z.array(z.number().int().positive()).optional(),
    removeGroupIdArr: z.array(z.number().int().positive()).optional(),
    removeFileIdArr: z.array(z.string().uuid()).optional(),
  })
  .refine(dueToAfterAvailable, {
    message: 'Data zakończenia musi być po dacie dostępności',
    path: ['dueTo'],
  });

export type AssignmentBuilderSchema = Infer<typeof assignmentBuilderSchema>;
export type AssignmentBuilderSchemaInput = Input<typeof assignmentBuilderSchema>;
export type AssignmentEditSchema = Infer<typeof assignmentEditSchema>;
export type AssignmentEditSchemaInput = Input<typeof assignmentEditSchema>;
